<template>
  <LayoutMain>
    <div class="space-y-8">
      <section class="space-y-2">
        <h1 class="font-headline-xl text-headline-xl text-primary">
          {{ t('extraView.title') }}
        </h1>
        <p class="max-w-2xl text-on-surface-variant">
          {{ t('extraView.description') }}
        </p>
      </section>

      <section class="space-y-3">
        <h2 class="font-headline text-headline-md text-primary">
          {{ t('extraView.recommended') }}
        </h2>
        <button
          class="flex w-full items-center gap-5 rounded-xl bg-primary p-6 text-left text-on-primary shadow-md transition hover:bg-primary-container active:scale-[0.99]"
          @click="goToDuoNamo"
        >
          <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/15">
            <Sparkles class="h-6 w-6" aria-hidden="true" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block font-headline text-headline-md text-on-primary">
              {{ t('extraView.duoNamo') }}
            </span>
            <span class="mt-1 block text-body-sm text-primary-fixed">
              {{ t('extraView.duoNamoDescription') }}
            </span>
          </span>
        </button>
      </section>

      <section class="secondary-group">
        <h2 class="secondary-heading">{{ t('extraView.connect') }}</h2>
        <button class="secondary-row" @click="goToPersonals">
          <Heart class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.personals') }}</span>
            <span class="secondary-meta">{{ t('extraView.personalsDescription') }}</span>
          </span>
        </button>
        <button class="secondary-row" @click="goToMyPersonals">
          <Newspaper class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.myPersonals') }}</span>
            <span class="secondary-meta">{{ t('extraView.myPersonalsDescription') }}</span>
          </span>
        </button>
        <button v-if="unreadInboxCount > 0" class="secondary-row" @click="goToMyReplies">
          <MessageCircle class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.myReplies') }}</span>
            <span class="secondary-meta">
              {{ t('extraView.unreadReplies', { count: unreadInboxCount }) }}
            </span>
          </span>
        </button>
      </section>

      <section class="secondary-group">
        <h2 class="secondary-heading">{{ t('extraView.play') }}</h2>
        <button class="secondary-row" @click="goToDuoNamo">
          <Sparkles class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.duoNamoShort') }}</span>
            <span class="secondary-meta">{{ t('extraView.duoNamoMeta') }}</span>
          </span>
        </button>
        <button class="secondary-row" @click="goToSevenFacts">
          <ListOrdered class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.sevenFacts') }}</span>
            <span class="secondary-meta">{{ t('extraView.sevenFactsMeta') }}</span>
          </span>
        </button>
        <button class="secondary-row" @click="goToMemoryGame">
          <Brain class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.playMemoryGame') }}</span>
            <span class="secondary-meta">{{ t('extraView.memoryGameMeta') }}</span>
          </span>
        </button>
      </section>

      <section class="secondary-group">
        <h2 class="secondary-heading">{{ t('extraView.account') }}</h2>
        <button class="secondary-row" @click="goToProfile">
          <User class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.myProfile') }}</span>
            <span class="secondary-meta">{{ t('extraView.profileDescription') }}</span>
          </span>
        </button>
        <button class="secondary-row" @click="router.push('/privacy-policy')">
          <LockKeyhole class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.privacyPolicy') }}</span>
            <span class="secondary-meta">{{ t('extraView.privacyDescription') }}</span>
          </span>
        </button>
        <button class="secondary-row text-on-surface-variant" @click="showLogoutConfirmation = true">
          <LogOut class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.logout') }}</span>
            <span class="secondary-meta">{{ t('extraView.logoutDescription') }}</span>
          </span>
        </button>
      </section>

      <section v-if="userStore.isAdmin" class="secondary-group">
        <h2 class="secondary-heading">{{ t('extraView.admin') }}</h2>
        <button class="secondary-row" @click="goToAdmin">
          <Shield class="secondary-icon" aria-hidden="true" />
          <span>
            <span class="secondary-title">{{ t('extraView.adminPanel') }}</span>
            <span class="secondary-meta">{{ t('extraView.adminDescription') }}</span>
          </span>
        </button>
      </section>

      <div
        v-if="showLogoutConfirmation"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      >
        <div class="w-full max-w-sm rounded-xl border border-outline-variant bg-surface-container-lowest p-6 shadow-xl">
          <div class="text-center">
            <AlertTriangle class="mx-auto mb-4 h-12 w-12 text-error" />
            <h3 class="mb-2 font-headline text-headline-md text-on-surface">
              {{ t('extraView.confirmLogoutTitle') }}
            </h3>
            <p class="mb-6 text-on-surface-variant">
              {{ t('extraView.confirmLogoutMessage') }}
            </p>
            <div class="flex gap-3">
              <Button variant="outline" class="flex-1" @click="showLogoutConfirmation = false">
                {{ t('extraView.cancel') }}
              </Button>
              <Button variant="danger" class="flex-1" :disabled="isLoggingOut" @click="handleLogout">
                <Loader2 v-if="isLoggingOut" class="mr-2 h-4 w-4 animate-spin" />
                {{ isLoggingOut ? t('extraView.loggingOut') : t('extraView.logout') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </LayoutMain>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useUserStore } from '../stores/user'
import { usePersonalsStore } from '../stores/personals'
import Button from '../components/ui/Button.vue'
import LayoutMain from '../components/ui/LayoutMain.vue'
import {
  Brain,
  ListOrdered,
  LogOut,
  AlertTriangle,
  Loader2,
  User,
  Shield,
  Sparkles,
  Heart,
  LockKeyhole,
  MessageCircle,
  Newspaper
} from 'lucide-vue-next'

const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const userStore = useUserStore()
const personalsStore = usePersonalsStore()

const showLogoutConfirmation = ref(false)
const isLoggingOut = ref(false)
const unreadInboxCount = computed(() => personalsStore.unreadInboxCount)

onMounted(async () => {
  try {
    await personalsStore.fetchInboxReplies()
  } catch (error) {
    console.error('Failed to fetch inbox replies:', error)
  }
})

const goToMemoryGame = () => router.push('/memory-game')
const goToSevenFacts = () => router.push('/seven-facts')
const goToDuoNamo = () => router.push('/duonamo')
const goToPersonals = () => router.push('/personals')
const goToMyPersonals = () => router.push('/my/personals')
const goToMyReplies = () => router.push('/my/replies')
const goToAdmin = () => router.push('/admin')

const goToProfile = () => {
  const userId = authStore.user?.id || userStore.profile?.id
  if (userId) router.push({ name: 'user-profile', params: { id: userId } })
}

const handleLogout = async () => {
  isLoggingOut.value = true

  try {
    await authStore.signOut()
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  } finally {
    isLoggingOut.value = false
    showLogoutConfirmation.value = false
  }
}
</script>

<style scoped>
.secondary-group {
  @apply space-y-2 rounded-xl border border-outline-variant bg-surface-container-lowest p-4 shadow-sm;
}

.secondary-heading {
  @apply px-2 pb-2 font-headline text-headline-md text-primary;
}

.secondary-row {
  @apply flex w-full items-center gap-4 rounded-lg px-3 py-3 text-left text-on-surface transition hover:bg-surface-container-low active:scale-[0.99];
}

.secondary-icon {
  @apply h-5 w-5 shrink-0 text-primary;
}

.secondary-title {
  @apply block font-label text-label-md text-on-surface;
}

.secondary-meta {
  @apply mt-0.5 block text-body-sm text-on-surface-variant;
}
</style>
